import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { ActivityIndicator, Text, TextInput } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useAppColors } from '../../hooks/useAppColors';
import { responsiveContentStyle } from '../../hooks/useResponsiveLayout';
import { useAuthStore, type PendingMember } from '../../stores/authStore';
import {
  useMembershipPlans,
  usePlanCatalog,
  useInitiateOrder,
  useUpdateOrder,
  type MembershipPlan,
} from '../../hooks/usePayments';
import { formatCurrency, calculateAge, weightToKg, heightToCm } from '../../utils/formatters';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import ScreenHeader from '../../components/common/ScreenHeader';
import LanguageSwitcher from '../../components/common/LanguageSwitcher';
import DatePicker from '../../components/DatePicker';
import PlanSelector from '../../components/billing/PlanSelector';
import TermSelector from '../../components/billing/TermSelector';
import PaymentMethodSelector from '../../components/billing/PaymentMethodSelector';
import {
  type SubUserFormData,
  calculateOfferPrice,
  getDiscountText,
  getMemberOffers,
  getOfferForPlanTerm,
  getAllowedTermsForMemberPlan,
  getPreselectedTermForMemberPlan,
  isDummyUUID,
  getNextDummyUUID,
} from '../../components/billing/billingUtils';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useLogout } from '../../hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { useValidateReferralCode, useMyReferral } from '../../hooks/useReferral';

const SKIP_PLAN_ID = 'skip';

interface MemberPlan {
  userId: string;
  name: string;
  dob: string | null;
  planId: string | null;
  termMonths: number;
}

export default function SubscriptionScreen() {
  const { t } = useTranslation();
  const colors = useAppColors();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const logout = useLogout();
  const { width: screenWidth } = useWindowDimensions();
  const isNarrow = screenWidth < 400;
  const user = useAuthStore((s) => s.user);
  const activeVietQRPayment = useAuthStore((s) => s.activeVietQRPayment);
  const setActiveVietQRPayment = useAuthStore((s) => s.setActiveVietQRPayment);
  const reusablePaymentId = useAuthStore((s) => s.reusablePaymentId);
  const setReusablePaymentId = useAuthStore((s) => s.setReusablePaymentId);
  const setNeedsSubscription = useAuthStore((s) => s.setNeedsSubscription);
  const subscriptionForm = useAuthStore((s) => s.subscriptionForm);
  const setSubscriptionForm = useAuthStore((s) => s.setSubscriptionForm);
  const clearSubscriptionForm = useAuthStore((s) => s.clearSubscriptionForm);
  const queryClient = useQueryClient();

  const scrollViewRef = useRef<ScrollView>(null);
  const [step, setStep] = useState<1 | 2>(subscriptionForm?.step || 1);
  const [familyDecisionMade, setFamilyDecisionMade] = useState<boolean>(subscriptionForm?.familyDecisionMade ?? false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Per-member term selection: userId → termMonths. Falls back to global default.
  const DEFAULT_TERM = 12;
  const [memberTermMonths, setMemberTermMonths] = useState<Record<string, number>>(
    subscriptionForm?.memberTermMonths || {}
  );
  // Derived: term for the main user (for backward compat + global TermSelector)
  const selectedTermMonths = user ? (memberTermMonths[user.id] ?? DEFAULT_TERM) : DEFAULT_TERM;
  const setSelectedTermMonths = (months: number) => {
    if (!user) return;
    setMemberTermMonths((prev) => ({ ...prev, [user.id]: months }));
  };
  const getMemberTerm = (userId: string) => memberTermMonths[userId] ?? DEFAULT_TERM;
  const setMemberTerm = (userId: string, months: number) =>
    setMemberTermMonths((prev) => ({ ...prev, [userId]: months }));

  // Main user plan selection
  const [mainPlanId, setMainPlanId] = useState<string | null>(subscriptionForm?.mainPlanId || null);
  const [isSkipping, setIsSkipping] = useState(subscriptionForm?.isSkipping || false);

  // Members and their selected plans (for new sub-users not yet created)
  const [pendingMembers, setPendingMembers] = useState<PendingMember[]>(subscriptionForm?.members || []);

  // Referral code state
  const [referralCodeInput, setReferralCodeInput] = useState(subscriptionForm?.referralCode || '');
  const [appliedReferralCode, setAppliedReferralCode] = useState<string | null>(subscriptionForm?.appliedReferralCode || null);
  const [appliedReferralName, setAppliedReferralName] = useState<string | null>(null);
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralError, setReferralError] = useState<string | null>(null);
  const { data: myReferral } = useMyReferral();
  const validateReferral = useValidateReferralCode();

  // Combined display list: main user + pending new members
  const members = useMemo(() => {
    const result: MemberPlan[] = [];
    // Add main user
    if (user) {
      result.push({
        userId: user.id,
        name: `${user.first_name} ${user.last_name}`,
        dob: user.dob,
        planId: mainPlanId,
        termMonths: getMemberTerm(user.id),
      });
    }
    // Add pending new members
    pendingMembers.forEach((pm) => {
      if (pm.userId && pm.firstName && pm.lastName) {
        result.push({
          userId: pm.userId,
          name: `${pm.firstName} ${pm.lastName}`,
          dob: pm.dob,
          planId: pm.planId,
          termMonths: getMemberTerm(pm.userId),
        });
      }
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, mainPlanId, pendingMembers, memberTermMonths]);

  // Modal state for adding family member
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [newMemberForm, setNewMemberForm] = useState<SubUserFormData>({
    firstName: '',
    lastName: '',
    dob: null,
    weightKg: null,
    heightCm: null,
    measurementUnit: 'metric',
  });
  const [weightInput, setWeightInput] = useState('');
  const [heightInput, setHeightInput] = useState('');

  const { data: basePlans, isLoading: plansLoading } = useMembershipPlans();

  // Collect all user IDs (payer + all dependents) for catalog fetch
  const allMemberIds = useMemo(() => {
    const ids: string[] = [];
    if (user) ids.push(user.id);
    pendingMembers.forEach((pm) => {
      if (pm.userId) ids.push(pm.userId);
    });
    return ids;
  }, [user, pendingMembers]);

  const { data: catalogData } = usePlanCatalog(allMemberIds.length > 0 ? allMemberIds : null);
  const catalogEntries = catalogData?.entries ?? [];

  // Merge inactive plans from catalog into the plans list
  const plans = useMemo(() => {
    if (!basePlans) return basePlans;
    const basePlanIds = new Set(basePlans.map((p) => p.id));
    // Find exclusive (hidden) plans from catalog that are targeted for a specific member
    const extraPlans: MembershipPlan[] = [];
    for (const entry of catalogEntries) {
      for (const offer of entry.offers) {
        if (offer.is_exclusive && !basePlanIds.has(offer.plan_id)) {
          extraPlans.push(offer.plan);
          basePlanIds.add(offer.plan_id);
        }
      }
    }
    return extraPlans.length > 0 ? [...basePlans, ...extraPlans] : basePlans;
  }, [basePlans, catalogEntries]);

  // Apply targeted pre-selections from catalog on first load
  useEffect(() => {
    if (!catalogEntries.length || !user) return;
    const updates: Record<string, number> = {};
    const pendingPlanUpdates: { userId: string; planId: string }[] = [];
    for (const entry of catalogEntries) {
      if (entry.preselected_plan_id) {
        const offers = entry.offers;
        const preselTerm = getPreselectedTermForMemberPlan(offers, entry.preselected_plan_id);
        if (preselTerm && !memberTermMonths[entry.user_id]) {
          updates[entry.user_id] = preselTerm;
        }
        // Pre-select the plan for the main user if not already set
        if (entry.user_id === user.id && !mainPlanId && !isSkipping) {
          setMainPlanId(entry.preselected_plan_id);
        }
        // Pre-select the plan for pending sub-users if not already set
        if (entry.user_id !== user.id) {
          const pm = pendingMembers.find((m) => m.userId === entry.user_id && !m.planId);
          if (pm) {
            pendingPlanUpdates.push({ userId: entry.user_id, planId: entry.preselected_plan_id });
          }
        }
      }
    }
    if (Object.keys(updates).length > 0) {
      setMemberTermMonths((prev) => ({ ...updates, ...prev }));
    }
    if (pendingPlanUpdates.length > 0) {
      setPendingMembers((prev) => prev.map((m) => {
        const update = pendingPlanUpdates.find((u) => u.userId === m.userId);
        return update ? { ...m, planId: update.planId } : m;
      }));
    }
  // Only run when catalogEntries first loads (not on every re-render)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogEntries.length > 0 ? catalogEntries[0]?.user_id : null]);

  const initiateOrder = useInitiateOrder();
  const updateOrder = useUpdateOrder();

  // Auto-redirect to VietQR screen if there's an active payment (refresh recovery)
  useEffect(() => {
    if (activeVietQRPayment) {
      const timer = setTimeout(() => {
        (navigation as any).navigate('VietQR', activeVietQRPayment);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeVietQRPayment, navigation]);

  // Sync subscription state to authStore for persistence
  useEffect(() => {
    setSubscriptionForm({
      mainPlanId,
      isSkipping,
      members: pendingMembers,
      removedSubUserIds: [],
      step,
      referralCode: referralCodeInput,
      appliedReferralCode,
      memberTermMonths,
      familyDecisionMade,
    });
  }, [mainPlanId, isSkipping, pendingMembers, step, referralCodeInput, appliedReferralCode, memberTermMonths, familyDecisionMade, setSubscriptionForm]);

  const handleOpenAddMemberModal = useCallback(() => {
    setNewMemberForm({ firstName: '', lastName: '', dob: null, weightKg: null, heightCm: null, measurementUnit: 'metric' });
    setWeightInput('');
    setHeightInput('');
    setShowAddMemberModal(true);
  }, []);

  const handleCloseAddMemberModal = useCallback(() => {
    setShowAddMemberModal(false);
    setNewMemberForm({ firstName: '', lastName: '', dob: null, weightKg: null, heightCm: null, measurementUnit: 'metric' });
    setWeightInput('');
    setHeightInput('');
  }, []);

  const handleConfirmAddMember = useCallback(() => {
    if (!newMemberForm.firstName.trim() || !newMemberForm.lastName.trim() || !newMemberForm.dob) return;
    const dobStr = newMemberForm.dob.toISOString().split('T')[0];
    const localId = getNextDummyUUID(pendingMembers.map((m) => m.userId));
    // Initialize new member's term to match main user's selected term
    setMemberTerm(localId, selectedTermMonths);
    setPendingMembers((prev) => [
      ...prev,
      {
        userId: localId,
        firstName: newMemberForm.firstName.trim(),
        lastName: newMemberForm.lastName.trim(),
        dob: dobStr,
        planId: null,
        termMonths: selectedTermMonths,
        weight_kg: newMemberForm.weightKg,
        height_cm: newMemberForm.heightCm,
        measurement_unit: newMemberForm.measurementUnit,
      },
    ]);
    setFamilyDecisionMade(true);
    handleCloseAddMemberModal();
    setTimeout(() => scrollViewRef.current?.scrollToEnd({ animated: true }), 100);
  }, [newMemberForm, selectedTermMonths, handleCloseAddMemberModal, pendingMembers, setMemberTerm]);

  const handleSelectPlan = useCallback((userId: string, planId: string) => {
    // Only pending members can have their plan changed in this flow
    setPendingMembers((prev) => prev.map((m) => (m.userId === userId ? { ...m, planId } : m)));
  }, []);

  const handleSelectMainPlan = useCallback((planId: string) => {
    if (planId === SKIP_PLAN_ID) {
      setIsSkipping(true);
      setMainPlanId(null);
    } else {
      setIsSkipping(false);
      setMainPlanId(planId);
    }
  }, []);

  const handleRemoveSubUser = useCallback((userId: string) => {
    // Only allow removing pending sub-users (dummy UUIDs), not the main user
    if (isDummyUUID(userId)) {
      setPendingMembers((prev) => {
        const next = prev.filter((m) => m.userId !== userId);
        // If all family members removed, reset the family decision so user must decide again
        if (next.length === 0) setFamilyDecisionMade(false);
        return next;
      });
    }
  }, []);

  // Check if at least one member has a plan selected (main user or any sub-user)
  const hasAnyPlanSelected = members.some((m) => m.planId);
  const allMembersHavePlans = members.length > 0 && members.every((m) => m.planId);
  // Allow checkout if: (1) all members have plans, OR (2) main user skipped AND all family members have plans
  const canProceedToCheckout = allMembersHavePlans || (isSkipping && pendingMembers.length > 0 && pendingMembers.every((pm) => pm.planId));
  // Reuse the same logic for Step 2 pay button and handleCheckout
  const canCompletePayment = canProceedToCheckout;
  const totalPrice = useMemo(() => {
    let total = 0;
    for (const m of members) {
      if (m.planId) {
        const plan = (plans || []).find((p: MembershipPlan) => p.id === m.planId);
        if (plan) {
          const termMonths = getMemberTerm(m.userId);
          const memberOffers = getMemberOffers(catalogEntries, m.userId);
          const offer = getOfferForPlanTerm(memberOffers, m.planId, termMonths);
          total += calculateOfferPrice(plan, termMonths, offer).finalAmount;
        }
      }
    }
    return total;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, plans, catalogEntries, memberTermMonths]);

  const handleCheckout = useCallback(async () => {
    if (!canCompletePayment || !user) return;
    if (referralCodeInput.trim() && !appliedReferralCode) {
      Alert.alert(
        t('referral.unappliedCodeTitle', { defaultValue: t('referral.checkoutPrompt') }),
        t('referral.unappliedCodeMessage', { defaultValue: 'You entered a referral code but haven\'t applied it. Tap "Apply" to use it, or clear the field to continue.' }),
        [
          { text: t('common.cancel'), style: 'cancel' },
          {
            text: t('referral.apply'),
            onPress: () => {
              setShowReferralInput(true);
            },
          },
        ]
      );
      return;
    }
    setIsProcessing(true);
    try {
      // Build memberships for existing users (main user + existing dependents)
      const memberships: { user_id: string; plan_id: string; term_months: number }[] = [];
      if (mainPlanId) {
        memberships.push({ user_id: user.id, plan_id: mainPlanId, term_months: getMemberTerm(user.id) });
      }
      // Existing sub-users (real UUIDs, not dummy placeholders)
      pendingMembers
        .filter((pm) => pm.userId && !isDummyUUID(pm.userId) && pm.planId)
        .forEach((pm) => {
          memberships.push({ user_id: pm.userId!, plan_id: pm.planId!, term_months: getMemberTerm(pm.userId!) });
        });

      // Build pending members list (sub-users created only after payment is verified)
      const pending_members = pendingMembers
        .filter((pm) => isDummyUUID(pm.userId) && pm.planId && pm.firstName && pm.lastName && pm.dob)
        .map((pm) => ({
          first_name: pm.firstName!,
          last_name: pm.lastName!,
          dob: pm.dob!,
          plan_id: pm.planId!,
          term_months: getMemberTerm(pm.userId!),
          weight_kg: pm.weight_kg,
          height_cm: pm.height_cm,
          measurement_unit: pm.measurement_unit,
        }));

      const orderPayload = {
        memberships,
        pending_members,
        language: i18n.language,
        referral_code: appliedReferralCode,
      };

      // Reuse the existing order if the user went back from the VietQR screen
      // earlier in this session; otherwise create a brand-new order. If the
      // update fails (e.g. the old payment was completed/expired/not found),
      // fall back to creating a fresh order so checkout never gets stuck.
      let result;
      let usedUpdate = false;
      const currentReusableId = useAuthStore.getState().reusablePaymentId;
      if (currentReusableId) {
        try {
          result = await updateOrder.mutateAsync({
            ...orderPayload,
            payment_id: currentReusableId,
          });
          usedUpdate = true;
        } catch (updateErr) {
          // Stale reusable id — clear it and fall through to create.
          setReusablePaymentId(null);
          result = await initiateOrder.mutateAsync(orderPayload);
        }
      } else {
        result = await initiateOrder.mutateAsync(orderPayload);
      }

      if (result.status === 'completed') {
        clearSubscriptionForm();
        setActiveVietQRPayment(null);
        setReusablePaymentId(null);
        setNeedsSubscription(false);
        queryClient.invalidateQueries();
        return;
      }

      // Track this order so a subsequent back-and-edit reuses it instead of
      // creating yet another payment.
      setReusablePaymentId(result.payment_id);

      const order_items = (result.order_items || []).map((item) => ({
        ...item,
        description: item.description
          ? t(`plans.${item.description}.name`, { defaultValue: item.description })
          : '',
      }));
      setActiveVietQRPayment({
        payment_id: result.payment_id,
        order_number: result.order_number,
        amount: parseFloat(result.amount),
        qr_url: result.qr_url,
        bank_bin: result.bank_bin,
        account_number: result.account_number,
        account_name: result.account_name,
        order_items,
      });
      (navigation as any).navigate('VietQR', {
        payment_id: result.payment_id,
        order_number: result.order_number,
        amount: result.amount,
        qr_url: result.qr_url,
        bank_bin: result.bank_bin,
        account_number: result.account_number,
        account_name: result.account_name,
        order_items,
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg = detail && detail.startsWith('errors.') ? t(detail, { defaultValue: detail }) : (detail || err?.message || t('billing.paymentError'));
      Alert.alert(t('billing.paymentError'), msg);
    } finally {
      setIsProcessing(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAnyPlanSelected, user, mainPlanId, pendingMembers, members, plans, memberTermMonths, initiateOrder, updateOrder, setActiveVietQRPayment, setReusablePaymentId, clearSubscriptionForm, navigation, t, appliedReferralCode]);

  if (plansLoading) return <LoadingIndicator />;

  const priceDisplay = formatCurrency(totalPrice);

  const stepTitle = step === 1 ? t('subscription.choosePlan') : t('billing.checkout');

  return (
    <View style={[styles.screen, { backgroundColor: colors.bg }]}>
      <ScreenHeader
        title={stepTitle}
        onBack={step > 1 ? () => setStep(1) : undefined}
        rightAction={step === 1 ? (
          <View style={styles.headerActions}>
            <LanguageSwitcher />
            <TouchableOpacity
              onPress={() => {
                if (Platform.OS === 'web') { if (window.confirm(t('common.logoutConfirm'))) logout(); }
                else Alert.alert(t('common.logout'), t('common.logoutConfirm'), [
                  { text: t('common.cancel'), style: 'cancel' },
                  { text: t('common.logout'), style: 'destructive', onPress: logout },
                ]);
              }}
              hitSlop={12}
            >
              <Icon name="logout" size={22} color={colors.text} />
            </TouchableOpacity>
          </View>
        ) : undefined}
      />

      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        contentContainerStyle={[styles.scrollContent, responsiveContentStyle, { paddingBottom: insets.bottom + 24 }]}
        showsVerticalScrollIndicator={false}
      >
        {step === 1 ? (
          /* ══════════════ STEP 1: CHOOSE YOUR PLAN ══════════════ */
          <>
            <View style={styles.heroSection}>
              <Text style={[styles.heroTitle, { color: colors.text }]}>{t('subscription.heroTitle')}</Text>
              <Text style={[styles.heroSubtitle, { color: colors.textSecondary }]}>{t('subscription.choosePlanSubtitle')}</Text>
            </View>

            {/* Duration Header */}
            <View style={[styles.sectionHeader, { borderBottomColor: colors.border }]}>
              <Text style={[styles.sectionLabel, { color: colors.textTertiary }]}>{t('subscription.duration')}</Text>
            </View>

            {/* Term Selector for main user — respects catalog allowed terms */}
            {(() => {
              const mainOffers = user ? getMemberOffers(catalogEntries, user.id) : [];
              const allowedTerms = getAllowedTermsForMemberPlan(mainOffers, mainPlanId);
              return (
                <TermSelector
                  selectedTermMonths={selectedTermMonths}
                  onSelectTerm={setSelectedTermMonths}
                  allowedTermMonths={allowedTerms}
                />
              );
            })()}

            {/* Plans Header */}
            <View style={[styles.sectionHeader, { borderBottomColor: colors.border }]}>
              <Text style={[styles.sectionLabel, { color: colors.textTertiary }]}>{t('subscription.plans')}</Text>
            </View>

            {/* Membership Plans Grid */}
            {(() => {
              const mainOffers = user ? getMemberOffers(catalogEntries, user.id) : [];
              const allowedInactivePlanIds = mainOffers
                .filter((o) => o.is_exclusive)
                .map((o) => o.plan_id);
              return (
                <PlanSelector
                  plans={plans}
                  dob={user?.dob || null}
                  selectedPlanId={isSkipping ? SKIP_PLAN_ID : mainPlanId}
                  onSelectPlan={handleSelectMainPlan}
                  variant="card"
                  showSkipOption={true}
                  skipPlanId={SKIP_PLAN_ID}
                  selectedTermMonths={selectedTermMonths}
                  showDiscountBadge={true}
                  allowedInactivePlanIds={allowedInactivePlanIds}
                  offers={mainOffers}
                />
              );
            })()}

            {/* Family Members Title */}
            {pendingMembers.length > 0 && (
              <View style={styles.heroSection}>
                <Text style={[styles.heroTitle, { color: colors.text }]}>{t('subscription.familyMembers')}</Text>
                <Text style={[styles.heroSubtitle, { color: colors.textSecondary }]}>{t('subscription.familyMembersSubtitle')}</Text>
              </View>
            )}

            {/* Family Members Sections */}
            {pendingMembers.filter((m) => !!m.userId).map((member) => (
              <View key={member.userId!} style={styles.familyMemberSection}>
                {/* Family Member Header with Name, Age, and Remove button */}
                <View style={[styles.familyMemberHeader, { borderBottomColor: colors.border }]}>
                  <View style={styles.familyMemberInfo}>
                    <Text style={[styles.familyMemberName, { color: colors.text }]}>
                      {member.firstName} {member.lastName}
                      {member.dob && (
                        <Text style={[styles.familyMemberAge, { color: colors.textSecondary }]}>
                          {' '}({calculateAge(member.dob!)} {t('common.years')})
                        </Text>
                      )}
                    </Text>
                  </View>
                  <TouchableOpacity
                    onPress={() => handleRemoveSubUser(member.userId!)}
                    hitSlop={12}
                    style={styles.removeButton}
                  >
                    <Icon name="close-circle" size={22} color={colors.textTertiary} />
                  </TouchableOpacity>
                </View>

                {/* Per-member term selector */}
                {(() => {
                  const mOffers = getMemberOffers(catalogEntries, member.userId!);
                  const memberPlanId = member.planId;
                  const allowedTerms = getAllowedTermsForMemberPlan(mOffers, memberPlanId);
                  return (
                    <TermSelector
                      selectedTermMonths={getMemberTerm(member.userId!)}
                      onSelectTerm={(months) => setMemberTerm(member.userId!, months)}
                      allowedTermMonths={allowedTerms}
                    />
                  );
                })()}

                {/* Plan Selector for this family member (card style, no skip option) */}
                {(() => {
                  const mOffers = getMemberOffers(catalogEntries, member.userId!);
                  const mAllowedInactivePlanIds = mOffers
                    .filter((o) => o.is_exclusive)
                    .map((o) => o.plan_id);
                  return (
                    <View style={styles.familyMemberPlans}>
                      {!member.planId && (
                        <View style={[styles.selectPlanNudge, { backgroundColor: colors.accent + '18', borderColor: colors.accent + '40' }]}>
                          <Icon name="arrow-down-circle-outline" size={16} color={colors.accent} />
                          <Text style={[styles.selectPlanNudgeText, { color: colors.accent }]}>
                            {t('subscription.selectPlanForMember', { name: member.firstName })}
                          </Text>
                        </View>
                      )}
                      <PlanSelector
                        plans={plans}
                        dob={member.dob ?? null}
                        selectedPlanId={member.planId}
                        onSelectPlan={(planId) => handleSelectPlan(member.userId!, planId)}
                        variant="card"
                        selectedTermMonths={getMemberTerm(member.userId!)}
                        showDiscountBadge={true}
                        allowedInactivePlanIds={mAllowedInactivePlanIds}
                        offers={mOffers}
                      />
                    </View>
                  );
                })()}
              </View>
            ))}

            {/* Family decision gate: shown when a plan is selected but user hasn't decided about family */}
            {/* When skipping, we don't show the family prompt - just show Add Family + disabled Checkout */}
            {mainPlanId && !familyDecisionMade && pendingMembers.length === 0 ? (
              <View style={[styles.familyPromptCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <Icon name="account-group" size={28} color={colors.accent} style={{ marginBottom: 10 }} />
                <Text style={[styles.familyPromptTitle, { color: colors.text }]}>
                  {t('subscription.familyPromptTitle')}
                </Text>
                <Text style={[styles.familyPromptSubtitle, { color: colors.textSecondary }]}>
                  {t('subscription.familyPromptSubtitle')}
                </Text>
                <View style={[styles.familyPromptButtons, isNarrow && styles.familyPromptButtonsNarrow]}>
                  <TouchableOpacity
                    style={[styles.familyPromptBtn, { backgroundColor: colors.accent }]}
                    onPress={handleOpenAddMemberModal}
                    activeOpacity={0.8}
                  >
                    <Text style={[styles.familyPromptBtnText, { color: colors.accentText }]}>
                      {t('subscription.familyPromptYes')}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.familyPromptBtn, { backgroundColor: colors.bg, borderColor: colors.border, borderWidth: 1 }]}
                    onPress={() => setFamilyDecisionMade(true)}
                    activeOpacity={0.8}
                  >
                    <Text style={[styles.familyPromptBtnText, { color: colors.text }]}>
                      {t('subscription.familyPromptNo')}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <>
                {/* Two buttons: Add Family + Checkout */}
                {/* When skipping, always show both buttons with Checkout disabled until family member added */}
                <View style={styles.stepOneButtons}>
                  {/* When skipping (checkout disabled), show Add Family first */}
                  {isSkipping && !canProceedToCheckout && (
                    <TouchableOpacity
                      style={[styles.stepOneBtn, { backgroundColor: colors.card, borderColor: colors.border, borderWidth: 1 }]}
                      onPress={handleOpenAddMemberModal}
                      activeOpacity={0.8}
                    >
                      <Icon name="account-multiple-plus" size={18} color={colors.text} style={{ marginRight: 6 }} />
                      <Text style={[styles.stepOneBtnText, { color: colors.text }]}>
                        {pendingMembers.length > 0 ? t('subscription.addAnotherMember') : t('subscription.addFamily')}
                      </Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity
                    style={[styles.stepOneBtn, { backgroundColor: colors.accent, opacity: canProceedToCheckout ? 1 : 0.5 }]}
                    onPress={() => setStep(2)}
                    disabled={!canProceedToCheckout}
                    activeOpacity={0.8}
                  >
                    <Text style={[styles.stepOneBtnText, { color: colors.accentText }]}>
                      {t('billing.checkout')}
                    </Text>
                  </TouchableOpacity>
                  {/* When not skipping or checkout is enabled, show Add Family after Checkout */}
                  {(!isSkipping || canProceedToCheckout) && familyDecisionMade && (
                    <TouchableOpacity
                      style={[styles.stepOneBtn, { backgroundColor: colors.card, borderColor: colors.border, borderWidth: 1 }]}
                      onPress={handleOpenAddMemberModal}
                      activeOpacity={0.8}
                    >
                      <Icon name="account-multiple-plus" size={18} color={colors.text} style={{ marginRight: 6 }} />
                      <Text style={[styles.stepOneBtnText, { color: colors.text }]}>
                        {pendingMembers.length > 0 ? t('subscription.addAnotherMember') : t('subscription.addFamily')}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>

                {/* Footnote when checkout is disabled */}
                {!canProceedToCheckout && (
                  <Text style={[styles.disabledFootnote, { color: colors.textTertiary }]}>
                    {t('subscription.selectMembershipToProceed')}
                  </Text>
                )}
              </>
            )}

            {/* Add Family Member Modal */}
            <Modal
              visible={showAddMemberModal}
              transparent={true}
              animationType="slide"
              onRequestClose={handleCloseAddMemberModal}
            >
              <KeyboardAvoidingView
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                style={styles.modalOverlay}
              >
                <View style={[styles.modalContent, { backgroundColor: colors.bg }]}>
                  <View style={styles.modalHeader}>
                    <Text style={[styles.modalTitle, { color: colors.text }]}>
                      {t('subscription.addFamilyMember')}
                    </Text>
                    <TouchableOpacity onPress={handleCloseAddMemberModal} hitSlop={12}>
                      <Icon name="close" size={24} color={colors.text} />
                    </TouchableOpacity>
                  </View>

                  <ScrollView style={styles.modalForm} contentContainerStyle={styles.modalFormContent} keyboardShouldPersistTaps="handled">
                    <View style={styles.row}>
                      <TextInput
                        label={t('auth.firstName')}
                        value={newMemberForm.firstName}
                        onChangeText={(text) => setNewMemberForm((prev) => ({ ...prev, firstName: text }))}
                        mode="outlined"
                        style={[styles.input, styles.halfInput]}
                        outlineColor={colors.border}
                        activeOutlineColor={colors.text}
                        textColor={colors.text}
                      />
                      <TextInput
                        label={t('auth.lastName')}
                        value={newMemberForm.lastName}
                        onChangeText={(text) => setNewMemberForm((prev) => ({ ...prev, lastName: text }))}
                        mode="outlined"
                        style={[styles.input, styles.halfInput]}
                        outlineColor={colors.border}
                        activeOutlineColor={colors.text}
                        textColor={colors.text}
                      />
                    </View>
                    <DatePicker
                      label={t('auth.dateOfBirth')}
                      value={newMemberForm.dob}
                      onChange={(date) => setNewMemberForm((prev) => ({ ...prev, dob: date }))}
                      maximumDate={new Date()}
                      minimumDate={new Date(1900, 0, 1)}
                    />

                    {/* Body Measurements */}
                    <View style={styles.row}>
                      <TextInput
                        label={`${t('profile.weight')} (${newMemberForm.measurementUnit === 'metric' ? 'kg' : 'lbs'})`}
                        value={weightInput}
                        onChangeText={(v) => {
                          setWeightInput(v);
                          const val = parseFloat(v);
                          setNewMemberForm((prev) => ({
                            ...prev,
                            weightKg: !isNaN(val) ? weightToKg(val, prev.measurementUnit) : null,
                          }));
                        }}
                        mode="outlined"
                        keyboardType="decimal-pad"
                        style={[styles.input, { flex: 1 }]}
                        outlineColor={colors.border}
                        activeOutlineColor={colors.text}
                        textColor={colors.text}
                      />
                      <TextInput
                        label={`${t('profile.height')} (${newMemberForm.measurementUnit === 'metric' ? 'cm' : 'in'})`}
                        value={heightInput}
                        onChangeText={(v) => {
                          setHeightInput(v);
                          const val = parseFloat(v);
                          setNewMemberForm((prev) => ({
                            ...prev,
                            heightCm: !isNaN(val) ? heightToCm(val, prev.measurementUnit) : null,
                          }));
                        }}
                        mode="outlined"
                        keyboardType="decimal-pad"
                        style={[styles.input, { flex: 1 }]}
                        outlineColor={colors.border}
                        activeOutlineColor={colors.text}
                        textColor={colors.text}
                      />
                      <TouchableOpacity
                        style={[styles.unitToggleBtn, { alignSelf: 'center' }]}
                        onPress={() => {
                          const newUnit = newMemberForm.measurementUnit === 'metric' ? 'imperial' : 'metric';
                          setNewMemberForm((prev) => ({ ...prev, measurementUnit: newUnit }));
                          setWeightInput('');
                          setHeightInput('');
                        }}
                      >
                        <Text style={[styles.unitToggleText, { color: colors.accent }]}>
                          {newMemberForm.measurementUnit === 'metric' ? t('profile.metric') : t('profile.imperial')}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </ScrollView>

                  <View style={styles.modalButtons}>
                    <TouchableOpacity
                      style={[styles.modalCancelBtn, { borderColor: colors.border }]}
                      onPress={handleCloseAddMemberModal}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.modalCancelBtnText, { color: colors.textSecondary }]}>
                        {t('common.cancel')}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.modalConfirmBtn,
                        { backgroundColor: colors.accent, opacity: (newMemberForm.firstName.trim() && newMemberForm.lastName.trim() && newMemberForm.dob) ? 1 : 0.5 }
                      ]}
                      onPress={handleConfirmAddMember}
                      disabled={!newMemberForm.firstName.trim() || !newMemberForm.lastName.trim() || !newMemberForm.dob}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.modalConfirmBtnText, { color: colors.accentText }]}>
                        {t('subscription.addMember')}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </KeyboardAvoidingView>
            </Modal>
          </>
        ) : (
          /* ══════════════ STEP 2: CHECKOUT ══════════════ */
          <>
              <View style={[styles.summaryCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <Text style={[styles.sectionLabel, { color: colors.textTertiary }]}>{t('billing.orderSummary')}</Text>
                {members.filter(m => m.planId).map((m) => {
                  const plan = m.planId ? (plans || []).find((p: MembershipPlan) => p.id === m.planId) : null;
                  const termMonths = getMemberTerm(m.userId);
                  const memberOffers = getMemberOffers(catalogEntries, m.userId);
                  const offer = getOfferForPlanTerm(memberOffers, m.planId, termMonths);
                  const pricing = plan ? calculateOfferPrice(plan, termMonths, offer) : null;
                  const hasSavings = pricing ? pricing.discountAmount > 0 : false;
                  return (
                    <View key={m.userId} style={styles.summaryRow}>
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.summaryPlan, { color: colors.text }]}>{m.name}</Text>
                        <Text style={[styles.summaryDesc, { color: colors.textSecondary }]}>
                          {plan ? t(`plans.${plan.machine_name}.name`, { defaultValue: plan.machine_name }) : ''}
                        </Text>
                        <Text style={[styles.summaryTerm, { color: colors.textTertiary }]}>
                          {termMonths} {termMonths === 1 ? t('common.month') : t('common.months')}
                        </Text>
                        {hasSavings && pricing?.discount && (
                          <Text style={[styles.summaryDiscount, { color: colors.accent }]}>
                            {getDiscountText(pricing.discount, pricing.subtotal, pricing.finalAmount, formatCurrency, t, termMonths)}
                          </Text>
                        )}
                      </View>
                      <View style={styles.summaryPriceCol}>
                        {hasSavings && pricing && (
                          <Text style={[styles.summaryPriceStrike, { color: colors.textTertiary }]}>
                            {formatCurrency(pricing.subtotal)}
                          </Text>
                        )}
                        <Text style={[styles.summaryPrice, { color: colors.text }]}>
                          {formatCurrency(pricing?.finalAmount || 0)}
                        </Text>
                      </View>
                    </View>
                  );
                })}
                <View style={[styles.summaryDivider, { borderTopColor: colors.border }]}>
                  <View style={styles.summaryTotal}>
                    <Text style={[styles.totalLabel, { color: colors.text }]}>{t('billing.totalDueToday')}</Text>
                    <Text style={[styles.totalValue, { color: colors.text }]}>{priceDisplay}</Text>
                  </View>
                </View>
              </View>

            {/* Referral Code Section - only show if user is eligible (no prior payments) and hasn't already redeemed a code */}
            {!myReferral?.redeemed_as_referee && (
              <View style={[styles.referralSection, { backgroundColor: colors.card, borderColor: colors.border }]}>
                {!appliedReferralCode ? (
                  <>
                    <TouchableOpacity
                      style={styles.referralPrompt}
                      onPress={() => setShowReferralInput(!showReferralInput)}
                    >
                      <Icon name="ticket-percent" size={18} color={colors.accent} />
                      <Text style={[styles.referralPromptText, { color: colors.accent }]}>
                        {t('referral.checkoutPrompt')}
                      </Text>
                      <Icon name={showReferralInput ? 'chevron-up' : 'chevron-down'} size={18} color={colors.accent} />
                    </TouchableOpacity>
                    {showReferralInput && (
                      <View style={styles.referralInputRow}>
                        <TextInput
                          value={referralCodeInput}
                          onChangeText={(text) => {
                            setReferralCodeInput(text);
                            if (referralError) setReferralError(null);
                          }}
                          placeholder="CBJJ-XXXXXX"
                          autoCapitalize="characters"
                          maxLength={12}
                          style={[styles.referralInput, { backgroundColor: colors.bg }]}
                          outlineColor={referralError ? '#ef4444' : colors.border}
                          activeOutlineColor={referralError ? '#ef4444' : colors.accent}
                          textColor={colors.text}
                          mode="outlined"
                          dense
                          error={!!referralError}
                        />
                        <TouchableOpacity
                          style={[styles.referralApplyBtn, { backgroundColor: colors.accent, opacity: referralCodeInput.length >= 6 ? 1 : 0.5 }]}
                          onPress={() => {
                            setReferralError(null);
                            validateReferral.mutate(referralCodeInput.toUpperCase(), {
                              onSuccess: (result) => {
                                if (result.valid && result.referrer) {
                                  // Check if referrer has active membership
                                  if (!result.referrer.is_active) {
                                    setReferralError(t('errors.referral.referrer_not_active'));
                                    return;
                                  }
                                  setAppliedReferralCode(referralCodeInput.toUpperCase());
                                  setAppliedReferralName(result.referrer.name);
                                  setShowReferralInput(false);
                                  setReferralError(null);
                                } else {
                                  const errorMsg = result.error_key
                                    ? t(result.error_key, { defaultValue: t('referral.invalidCode') })
                                    : t('referral.invalidCode');
                                  setReferralError(errorMsg);
                                }
                              },
                              onError: (err: any) => {
                                const detail = err?.response?.data?.detail;
                                const msg = detail && detail.startsWith('errors.') ? t(detail, { defaultValue: detail }) : (detail || err?.message || t('referral.invalidCode'));
                                setReferralError(msg);
                              },
                            });
                          }}
                          disabled={referralCodeInput.length < 6 || validateReferral.isPending}
                        >
                          {validateReferral.isPending ? (
                            <ActivityIndicator size="small" color={colors.accentText} />
                          ) : (
                            <Text style={[styles.referralApplyText, { color: colors.accentText }]}>
                              {t('referral.apply')}
                            </Text>
                          )}
                        </TouchableOpacity>
                      </View>
                    )}
                    {referralError && (
                      <Text style={[styles.referralErrorText, { color: '#ef4444' }]}>
                        {referralError}
                      </Text>
                    )}
                  </>
                ) : (
                  <View style={[styles.referralApplied, { backgroundColor: 'rgba(34,197,94,0.08)' }]}>
                    <Icon name="check-circle" size={18} color="#22c55e" />
                    <View style={{ flex: 1, marginLeft: 8 }}>
                      <Text style={[styles.referralAppliedText, { color: colors.text }]}>
                        {t('referral.appliedSuccess', { name: appliedReferralName || t('common.loading') })}
                      </Text>
                    </View>
                    <TouchableOpacity
                      onPress={() => {
                        setAppliedReferralCode(null);
                        setAppliedReferralName(null);
                        setReferralCodeInput('');
                      }}
                    >
                      <Icon name="close" size={18} color={colors.textSecondary} />
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            )}

            {/* Reward chip - shows when referral is applied */}
            {appliedReferralCode && (
              <View style={[styles.rewardChip, { backgroundColor: colors.accent + '15', borderColor: colors.accent + '40' }]}>
                <Icon name="gift-outline" size={14} color={colors.accent} />
                <Text style={[styles.rewardChipText, { color: colors.accent }]}>
                  {t('referral.rewardChip')}
                </Text>
              </View>
            )}

            <View>
              {/* VietQR info panel */}
              <View style={[styles.payPanel, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={[styles.securityBadge, { backgroundColor: 'rgba(34,197,94,0.12)', borderColor: 'rgba(34,197,94,0.3)' }]}>
                  <Icon name="shield-check" size={12} color="#22c55e" />
                  <Text style={{ color: '#22c55e', fontSize: 11, fontWeight: '700', marginLeft: 6 }}>{t('vietqr.securedByVietQR')}</Text>
                </View>
                <Text style={[styles.panelTitle, { color: colors.text }]}>{t('vietqr.pushPaymentTitle')}</Text>
                <Text style={[styles.panelSubtitle, { color: colors.textSecondary }]}>{t('vietqr.pushPaymentSubtitle')}</Text>
                <View style={[styles.noticeBox, { backgroundColor: colors.bg, borderColor: colors.border }]}>
                  <Icon name="information-outline" size={16} color={colors.accent} style={{ marginTop: 2 }} />
                  <View style={{ flex: 1, marginLeft: 10 }}>
                    <Text style={[styles.noticeTitle, { color: colors.text }]}>{t('vietqr.noAutoDeduct')}</Text>
                    <Text style={[styles.noticeBody, { color: colors.textSecondary }]}>{t('vietqr.noAutoDeductDesc')}</Text>
                  </View>
                </View>
              </View>

              <PaymentMethodSelector
                enabled={false}
                paymentRoute={'vietqr'}
                setPaymentRoute={() => {}}
                saveCard={false}
                setSaveCard={() => {}}
                selectedToken={null}
                setSelectedToken={() => {}}
                savedCards={[]}
                isProcessing={false}
                planId={mainPlanId}
                plans={plans}
                onPay={() => {}}
                callbackUrl={''}
              />

              <TouchableOpacity
                style={[styles.payBtn, { backgroundColor: colors.accent, opacity: (isProcessing || !canCompletePayment) ? 0.6 : 1 }]}
                activeOpacity={0.8}
                onPress={handleCheckout}
                disabled={isProcessing || !canCompletePayment}
              >
                {isProcessing ? (
                  <ActivityIndicator size="small" color={colors.accentText} />
                ) : (
                  <Text style={[styles.payBtnText, { color: colors.accentText }]}>
                    {t('billing.payAmount', { amount: priceDisplay })}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </>
        )}
      </ScrollView>
    </View>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  screen: { flex: 1 },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  scrollView: { flex: 1 },
  scrollContent: { padding: 20, gap: 16 },

  // Hero Section
  heroSection: {
    paddingVertical: 8,
  },
  heroTitle: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  heroSubtitle: {
    fontSize: 14,
  },

  // Section Header with Divider
  sectionHeader: {
    paddingBottom: 10,
    borderBottomWidth: 1,
    marginBottom: 6,
  },

  // Plan Card (for skip option)
  planCard: {
    width: '100%',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1.5,
    position: 'relative',
  },
  planHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  planIndex: {
    fontSize: 12,
    fontWeight: '600',
    opacity: 0.6,
  },
  planName: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 4,
  },
  planDesc: {
    fontSize: 12,
    lineHeight: 16,
  },
  selectedIndicator: {
    position: 'absolute',
    top: 10,
    right: 10,
  },

  // Pay Button
  payBtn: {
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  payBtnText: {
    fontSize: 16,
    fontWeight: '600',
  },

  // Summary Card
  summaryCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  summaryPlan: {
    fontSize: 15,
    fontWeight: '500',
  },
  summaryDesc: {
    fontSize: 13,
    marginTop: 2,
  },
  summaryTerm: {
    fontSize: 12,
    marginTop: 2,
  },
  summaryPriceCol: {
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  summaryPriceStrike: {
    fontSize: 11,
    textDecorationLine: 'line-through',
  },
  summaryPrice: {
    fontSize: 16,
    fontWeight: '600',
  },
  summaryDiscount: {
    fontSize: 11,
    fontWeight: '500',
    marginTop: 2,
  },
  summaryDivider: {
    paddingTop: 12,
    borderTopWidth: 1,
    marginTop: 4,
  },
  summaryTotal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalLabel: {
    fontSize: 15,
    fontWeight: '600',
  },
  totalValue: {
    fontSize: 18,
    fontWeight: '700',
  },

  // Step 1 dual buttons
  stepOneButtons: {
    flexDirection: 'row',
    gap: 10,
  },
  stepOneBtn: {
    flex: 1,
    flexDirection: 'row',
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepOneBtnText: {
    fontSize: 15,
    fontWeight: '600',
  },
  disabledFootnote: {
    fontSize: 13,
    textAlign: 'center',
    marginTop: 12,
    fontStyle: 'italic',
  },

  // Select plan nudge banner
  selectPlanNudge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 10,
  },
  selectPlanNudgeText: {
    fontSize: 13,
    fontWeight: '600',
    flex: 1,
  },

  // Family prompt card
  familyPromptCard: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 20,
    alignItems: 'center',
  },
  familyPromptTitle: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 6,
  },
  familyPromptSubtitle: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 18,
    lineHeight: 20,
  },
  familyPromptButtons: {
    flexDirection: 'row',
    gap: 10,
    width: '100%',
  },
  familyPromptButtonsNarrow: {
    flexDirection: 'column',
  },
  familyPromptBtn: {
    flex: 1,
    flexDirection: 'row',
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  familyPromptBtnText: {
    fontSize: 15,
    fontWeight: '600',
  },

  // Term selector buttons
  termButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    minWidth: 60,
    alignItems: 'center',
  },
  termButtonText: {
    fontSize: 12,
    fontWeight: '600',
  },

  // Family Member Section
  familyMemberSection: {
    marginBottom: 16,
  },
  familyMemberHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
  },
  familyMemberInfo: {
    flex: 1,
  },
  familyMemberName: {
    fontSize: 16,
    fontWeight: '600',
  },
  familyMemberAge: {
    fontSize: 14,
    fontWeight: '400',
  },
  removeButton: {
    padding: 4,
    marginLeft: 8,
  },
  familyMemberPlans: {
    marginTop: 8,
  },
  selectPlanLabel: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    marginBottom: 12,
  },

  // Add Family Modal
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  modalForm: {
    maxHeight: '70%',
  },
  modalFormContent: {
    gap: 12,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
  },
  modalCancelBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
  },
  modalCancelBtnText: {
    fontSize: 15,
    fontWeight: '600',
  },
  modalConfirmBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  modalConfirmBtnText: {
    fontSize: 15,
    fontWeight: '700',
  },
  row: {
    flexDirection: 'row',
    gap: 10,
  },
  input: {
    flex: 1,
  },
  halfInput: {
    flex: 1,
  },

  // Checkout Member List
  checkoutMemberList: {
    gap: 8,
  },
  checkoutMemberItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  checkoutMemberName: {
    fontSize: 15,
    fontWeight: '500',
  },
  checkoutMemberDob: {
    fontSize: 12,
    marginTop: 2,
  },
  checkoutMemberPlan: {
    fontSize: 13,
    fontWeight: '600',
    marginTop: 4,
  },
  planSelectorDropdown: {
    marginTop: 8,
  },

  // VietQR Panel
  payPanel: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  securityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    marginBottom: 12,
  },
  panelTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  panelSubtitle: {
    fontSize: 13,
    marginBottom: 12,
  },
  noticeBox: {
    flexDirection: 'row',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  noticeTitle: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 2,
  },
  noticeBody: {
    fontSize: 12,
    lineHeight: 16,
  },

  // Payment Options
  paymentOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 14,
    borderRadius: 8,
    borderWidth: 1,
  },
  paymentOptionLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  paymentOptionTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  paymentOptionDesc: {
    fontSize: 12,
    marginTop: 2,
  },

  // ─── OnePay Styles (from BillingScreen) ──────────────────────────────────────
  paymentTitle: { fontSize: 17, fontWeight: '700', marginTop: 8 },

  payOption: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioDot: { width: 10, height: 10, borderRadius: 5 },
  payOptionRow: { flexDirection: 'row', alignItems: 'center' },
  payOptionTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  payOptionTitle: { fontSize: 14, fontWeight: '700' },
  payOptionSub: { fontSize: 11, marginTop: 2, marginLeft: 22 },
  payBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 4 },
  payBadgeText: { fontSize: 9, fontWeight: '800', letterSpacing: 0.5 },

  savedCardsLabel: { fontSize: 10, fontWeight: '700', letterSpacing: 1.5, marginTop: 12 },

  // Card panel
  cardPanelHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: 20,
  },
  cardPanelTitle: { fontSize: 18, fontWeight: '700' },
  cardBrands: { flexDirection: 'row', gap: 8 },

  redirectNotice: {
    flexDirection: 'row',
    borderRadius: 14,
    borderWidth: 1,
    padding: 14,
    width: '100%',
    marginBottom: 16,
  },

  tokenInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    paddingVertical: 12,
    marginBottom: 8,
  },
  tokenLabel: { fontSize: 15, fontWeight: '700' },
  tokenSub: { fontSize: 12, marginTop: 2 },

  tokenCheckRow: {
    flexDirection: 'row',
    borderRadius: 14,
    borderWidth: 1,
    padding: 14,
    width: '100%',
    alignItems: 'flex-start',
  },
  tokenCheckTitle: { fontSize: 13, fontWeight: '700' },
  tokenCheckBody: { fontSize: 11, lineHeight: 17, marginTop: 2 },

  // Referral section
  referralSection: {
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 8,
  },
  referralPrompt: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 4,
  },
  referralPromptText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
  },
  referralInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  referralInput: {
    flex: 1,
    height: 44,
  },
  referralApplyBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  referralApplyText: {
    fontSize: 14,
    fontWeight: '600',
  },
  referralErrorText: {
    fontSize: 13,
    marginTop: 6,
    marginLeft: 4,
  },
  referralApplied: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
  },
  referralAppliedText: {
    fontSize: 13,
    lineHeight: 18,
  },
  rewardChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    marginTop: 8,
  },
  rewardChipText: {
    fontSize: 12,
    fontWeight: '600',
  },
  unitToggleBtn: {
    paddingHorizontal: 12,
    paddingVertical: 14,
    borderRadius: 8,
    backgroundColor: 'transparent',
  },
  unitToggleText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
